/**
 * Collections (courses with usage TEMPLATE) are visually distinguished from
 * regular courses by a generated gradient instead of a flat color. The
 * gradient is derived deterministically from the two colors stored on the
 * course, so it stays stable everywhere the collection is shown. "Changing"
 * the gradient works by re-rolling the stored color pair with random values —
 * there is deliberately no picker.
 */
import { computed, type ComputedRef } from 'vue';

export type CollectionColorPair = {
  foregroundColor: string;
  backgroundColor: string;
};

/**
 * Fixed Template Grey used for all collections before TE-2741 random colors.
 *
 * Release TODO: create a blank MikroORM migration and backfill TEMPLATE courses
 * still on this pair with random colors matching `generateRandomCollectionColors()`
 * (same HSL rules: hue offset 45°, bg 80/80, fg 65/30).
 */
export const LEGACY_TEMPLATE_COLLECTION_COLORS: CollectionColorPair = {
  foregroundColor: '#494A50',
  backgroundColor: '#E7E9F4',
};

const FULL_CIRCLE_DEGREES = 360;
const HUE_OFFSET_DEGREES = 45;
const HASH_PRIME = 31;
const HASH_MASK = 0x7fffffff;
const CENTER_X_MIN = 25;
const CENTER_X_RANGE = 50;
const CENTER_Y_MIN = 20;
const CENTER_Y_RANGE = 30;
const CENTER_Y_HASH_SHIFT = 4;
const BACKGROUND_SATURATION = 80;
const BACKGROUND_LIGHTNESS = 80;
const FOREGROUND_SATURATION = 65;
const FOREGROUND_LIGHTNESS = 30;

/**
 * Derives a stable pseudo-random number from the color pair so gradient
 * shape parameters (angle, center) vary between collections without storing
 * extra data.
 */
const hashColors = (colors: CollectionColorPair): number => {
  const input = `${colors.foregroundColor}${colors.backgroundColor}`;
  let hash = 0;
  for (const char of input) {
    // eslint-disable-next-line no-bitwise
    hash = (hash * HASH_PRIME + char.charCodeAt(0)) & HASH_MASK;
  }
  return hash;
};

export const buildCollectionGradient = (
  colors: CollectionColorPair,
): string => {
  const hash = hashColors(colors);
  const angle = String(hash % FULL_CIRCLE_DEGREES);
  const centerX = String(CENTER_X_MIN + (hash % CENTER_X_RANGE));
  const centerY = String(
    // eslint-disable-next-line no-bitwise
    CENTER_Y_MIN + ((hash >> CENTER_Y_HASH_SHIFT) % CENTER_Y_RANGE),
  );
  const { foregroundColor, backgroundColor } = colors;

  return [
    `conic-gradient(from ${angle}deg at ${centerX}% ${centerY}%, ${foregroundColor}, ${backgroundColor}, rgba(255, 255, 255, 0.55), ${backgroundColor}, ${foregroundColor})`,
    `linear-gradient(145deg, ${backgroundColor}, ${foregroundColor})`,
  ].join(', ');
};

/**
 * Guards a possibly-absent color source and returns the derived gradient (or
 * `undefined` while the source is missing). Centralises the null-check +
 * `buildCollectionGradient` boilerplate the collection views share.
 */
export const useCollectionGradient = (
  getColors: () => CollectionColorPair | null | undefined,
): ComputedRef<string | undefined> =>
  computed(() => {
    const colors = getColors();
    return colors ? buildCollectionGradient(colors) : undefined;
  });

/* eslint-disable @typescript-eslint/no-magic-numbers -- standard HSL→RGB conversion formula */
const hslToHex = (hue: number, saturation: number, lightness: number) => {
  const sat = saturation / 100;
  const light = lightness / 100;
  const k = (n: number) => (n + hue / 30) % 12;
  const a = sat * Math.min(light, 1 - light);
  const f = (n: number) =>
    light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (value: number) =>
    Math.round(value * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
};
/* eslint-enable @typescript-eslint/no-magic-numbers */

/**
 * Rolls a fresh random color pair for a collection: a light background tone
 * and a dark foreground tone on nearby hues, so the derived gradient stays
 * readable with white text on the dark end.
 */
export const generateRandomCollectionColors = (): CollectionColorPair => {
  const hue = Math.floor(Math.random() * FULL_CIRCLE_DEGREES);
  const foregroundHue = (hue + HUE_OFFSET_DEGREES) % FULL_CIRCLE_DEGREES;

  return {
    backgroundColor: hslToHex(hue, BACKGROUND_SATURATION, BACKGROUND_LIGHTNESS),
    foregroundColor: hslToHex(
      foregroundHue,
      FOREGROUND_SATURATION,
      FOREGROUND_LIGHTNESS,
    ),
  };
};
